import { motion } from "framer-motion";
import { Link, Brain, FileText, ArrowRight, Play } from "lucide-react";
import Section from "../ui/Section";
import Card from "../ui/Card";
import Badge from "../ui/Badge";
import Button from "../ui/Button";

const steps = [
  {
    number: "01",
    title: "Bağlan",
    subtitle: "Sistem Entegrasyonu",
    description:
      "MultiVision Report'u mevcut PACS/HIS sisteminize kolayca entegre edin. FastAPI + WebSocket altyapısı ile güvenli veri transferi. Docker konteyner ile plug-and-play kurulum.",
    icon: Link,
    details: [
      "PACS/HIS sistemleri ile API entegrasyonu",
      "Docker 24+ ile tek komutta kurulum",
      "TLS 1.3 güvenli veri transferi",
      "NGINX reverse proxy ile yük dengeleme",
    ],
    gradient: "from-primary-500 to-indigo-600",
    techNote: "FastAPI + WebSocket + Docker + NGINX",
  },
  {
    number: "02",
    title: "Analiz",
    subtitle: "AI Destekli İşleme",
    description:
      "Özel eğitilmiş AI model konuşmanızı ~200ms'de analiz eder. Dil modeli ile Türkçe tıbbi terminoloji doğruluğu %100. Çok katmanlı NLP pipeline bilgiyi yapılandırır.",
    icon: Brain,
    details: [
      "Optimize AI model: 360MB, ~200ms işleme",
      "Otomatik varlık tanıma ve ilişki çıkarımı",
      "Hibrit AI yaklaşım (~200 örnek yeterli)",
      "SNOMED CT + ICD-10 otomatik eşleştirme",
    ],
    gradient: "from-violet-500 to-purple-600",
    techNote: "AI Konuşma Tanıma + Dil Modeli + NLP",
  },
  {
    number: "03",
    title: "Raporla",
    subtitle: "Otomatik Rapor Oluşturma",
    description:
      "3 seviyeli yanıt mimarisi ile anında rapor. Önbellek (%75 anında yanıt, <1ms), paralel arama (<80ms), AI destekli oluşturma (<2s). Toplam yanıt süresi <2.1 saniye.",
    icon: FileText,
    details: [
      "Akıllı önbellekleme: %75 anında yanıt",
      "Hızlı benzerlik araması: 42ms",
      "1,200 eşzamanlı kullanıcı, 1.8GB bellek",
      "PDF, Word, HL7 formatında dışa aktarım",
    ],
    gradient: "from-emerald-500 to-teal-600",
    techNote: "Önbellek + Arama + AI Rapor Oluşturma",
  },
];

export default function HowItWorks() {
  return (
    <Section id="how-it-works" background="mesh" padding="xl">
      <div className="text-center mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Badge variant="primary" size="lg" className="mb-6">
            <Play size={16} className="mr-2" />
            Nasıl Çalışır?
          </Badge>
          <h2 className="text-display-sm md:text-display-md font-display font-bold text-gray-900 dark:text-white mb-5">
            Sadece <span className="text-gradient">3 Adım</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Entegrasyon, analiz ve raporlama - tümü gerçek zamanlı, tümü doğrulanmış performansla
          </p>
        </motion.div>
      </div>

      {/* Steps */}
      <div className="relative">
        {/* Vertical connecting line */}
        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary-300/50 via-violet-300/50 to-emerald-300/50 dark:from-primary-600/30 dark:via-violet-600/30 dark:to-emerald-600/30 transform -translate-x-1/2 z-0" />

        <div className="space-y-12 lg:space-y-16">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.15 }}
                className="relative"
              >
                {/* Step number on the line (desktop) */}
                <div className="hidden lg:flex absolute left-1/2 top-8 transform -translate-x-1/2 z-20">
                  <div
                    className={`w-12 h-12 rounded-full bg-gradient-to-br ${step.gradient} flex items-center justify-center text-white font-bold text-sm shadow-lg ring-4 ring-white dark:ring-navy-900`}
                  >
                    {step.number}
                  </div>
                </div>

                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                    isEven ? "" : "lg:grid-flow-col-dense"
                  }`}
                >
                  {/* Content */}
                  <div className={`${isEven ? "lg:pr-16" : "lg:pl-16 lg:col-start-2"}`}>
                    <div className="flex items-center space-x-4 mb-5">
                      <div
                        className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.gradient} flex items-center justify-center text-white shadow-lg lg:hidden`}
                      >
                        {step.number}
                      </div>
                      <div>
                        <h3 className="text-2xl font-display font-bold text-gray-900 dark:text-white">
                          {step.title}
                        </h3>
                        <p className="text-sm text-primary-600 dark:text-primary-400 font-medium">
                          {step.subtitle}
                        </p>
                      </div>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                      {step.description}
                    </p>

                    {/* Tech note */}
                    <div className="mb-5 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                      <span className="text-xs font-mono text-gray-500 dark:text-gray-500">
                        {step.techNote}
                      </span>
                    </div>

                    <div className="space-y-2.5">
                      {step.details.map((detail, detailIndex) => (
                        <motion.div
                          key={detail}
                          initial={{ opacity: 0, x: isEven ? -15 : 15 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0.4,
                            delay: index * 0.15 + detailIndex * 0.06,
                          }}
                          className="flex items-center space-x-3"
                        >
                          <div
                            className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${step.gradient} flex-shrink-0`}
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {detail}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Visual Card */}
                  <div className={`${isEven ? "" : "lg:col-start-1"} flex justify-center`}>
                    <Card variant="glass" className="w-full max-w-sm" padding="none">
                      <div
                        className={`h-44 rounded-t-2xl bg-gradient-to-br ${step.gradient} flex items-center justify-center relative overflow-hidden`}
                      >
                        <Icon size={56} className="text-white/80 z-10" />
                        <div className="absolute inset-0 shimmer" />
                        <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full" />
                        <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-white/5 rounded-full" />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white">
                            {step.title}
                          </h4>
                          <span className="text-xs font-mono text-gray-400">
                            Adım {step.number}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {step.subtitle}
                        </p>
                      </div>
                    </Card>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Demo CTA */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="text-center mt-16"
      >
        <Card variant="glass" padding="lg" className="text-center">
          <h3 className="text-xl md:text-2xl font-display font-bold text-gray-900 dark:text-white mb-3">
            Canlı Demo ile Deneyimleyin
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
            3 adımlı sürecin tamamını canlı demo ile test edin.
            Uzmanlarımız size özel bir sunum hazırlayacak.
          </p>
          <Button
            onClick={() =>
              document
                .getElementById("contact")
                ?.scrollIntoView({ behavior: "smooth" })
            }
          >
            Ücretsiz Demo Talep Et
            <ArrowRight size={18} />
          </Button>
        </Card>
      </motion.div>
    </Section>
  );
}
